import React, { useEffect, useState, useMemo } from 'react';
import DeskLayout from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Archive, FileText, Copy, Eye, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { listContentArchive, getContentArchiveSnapshot } from '../../lib/api';

// Strip JSX/JS noise to reveal the human-readable copy. Very lightweight —
// removes import/export lines, JSX tags, attributes, common syntactic glue,
// and collapses whitespace. Good enough for an at-a-glance audit; the user
// can always switch to the raw JSX tab for the source of truth.
const extractHumanCopy = (jsxText) => {
  if (!jsxText) return '';
  let s = jsxText;
  // Remove block comments & line comments
  s = s.replace(/\/\*[\s\S]*?\*\//g, '');
  s = s.replace(/\/\/[^\n]*\n/g, '\n');
  // Drop import/export lines
  s = s.replace(/^\s*import [^\n]+\n/gm, '');
  s = s.replace(/^\s*export [^\n]+\n/gm, '');
  // Strip JSX tags but keep their text content
  s = s.replace(/<\/?[A-Za-z][^>]*>/g, '\n');
  // Strip {expressions} – keep nested string literals if present
  s = s.replace(/\{[^{}\n]*\}/g, ' ');
  // Strip leftover JS punctuation that isn't typographically meaningful
  s = s.replace(/[`;]/g, '');
  // Collapse runs of whitespace
  s = s.replace(/\n{3,}/g, '\n\n');
  s = s.replace(/[ \t]+/g, ' ');
  // Trim each line and drop empties / single-character noise lines
  s = s
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && line.length > 1 && !/^[(){}[\],=.<>+\-*/!&|?:]+$/.test(line))
    .join('\n');
  return s;
};

const ContentArchive = () => {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [archiveDescription, setArchiveDescription] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [viewMode, setViewMode] = useState('copy'); // 'copy' | 'raw'

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listContentArchive();
        if (cancelled) return;
        setEntries(res?.data?.entries || []);
        setArchiveDescription(res?.data?.description || '');
      } catch (e) {
        if (!cancelled) toast.error('Could not load the archive index');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const openSnapshot = async (id) => {
    setSelectedId(id);
    setSnapshot(null);
    setSnapshotLoading(true);
    try {
      const res = await getContentArchiveSnapshot(id);
      setSnapshot(res?.data || null);
    } catch (e) {
      toast.error('Could not load that snapshot');
    } finally {
      setSnapshotLoading(false);
    }
  };

  const entriesByPage = useMemo(() => {
    const out = {};
    for (const e of entries) {
      const k = e.page || 'Other';
      (out[k] ||= []).push(e);
    }
    Object.values(out).forEach((arr) => arr.sort((a, b) => (a.date || '').localeCompare(b.date || '')));
    return out;
  }, [entries]);

  const visibleText = useMemo(() => {
    if (!snapshot) return '';
    return viewMode === 'copy' ? extractHumanCopy(snapshot.content) : snapshot.content;
  }, [snapshot, viewMode]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(visibleText);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  return (
    <DeskLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="content-archive-page">
        {/* Header */}
        <div className="flex items-start gap-3">
          <Archive className="h-5 w-5 text-[#3F617E] mt-1 shrink-0" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Content Archive</h1>
            <p className="text-stone-500 mt-1 text-sm">
              Read-only record of pre-cleanup public-facing copy. The live /home and /request-review pages are untouched by this page.
            </p>
            {archiveDescription && (
              <p className="text-xs text-stone-400 mt-2 leading-relaxed max-w-3xl">{archiveDescription}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          {/* Left: index */}
          <div className="space-y-4">
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              Object.entries(entriesByPage).map(([page, list]) => (
                <Card key={page} className="border-stone-200">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm font-heading text-stone-900 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#3F617E]" />
                      {page === 'Home' ? 'Public Homepage' : page === 'RequestReview' ? 'Request a Review' : page}
                    </CardTitle>
                    <CardDescription className="text-[12px]">
                      {list.length} snapshot{list.length === 1 ? '' : 's'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-1.5">
                    {list.map((e) => {
                      const active = e.id === selectedId;
                      return (
                        <button
                          key={e.id}
                          onClick={() => openSnapshot(e.id)}
                          className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                            active
                              ? 'border-[#3F617E] bg-[#3F617E]/8 ring-1 ring-[#3F617E]/30'
                              : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                          }`}
                          data-testid={`archive-entry-${e.id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[13px] font-medium text-stone-900 truncate">{e.label}</span>
                            <Badge variant="outline" className="text-[10px] font-mono shrink-0">{e.date}</Badge>
                          </div>
                          <p className="text-[11.5px] text-stone-500 mt-0.5 line-clamp-2">{e.summary}</p>
                        </button>
                      );
                    })}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Right: viewer */}
          <div>
            {!selectedId && !snapshotLoading && (
              <Card className="border-stone-200 border-dashed">
                <CardContent className="p-10 text-center">
                  <Eye className="h-6 w-6 mx-auto text-stone-300 mb-3" />
                  <p className="text-sm text-stone-500">Select a snapshot on the left to view its content.</p>
                  <p className="text-[12px] text-stone-400 mt-1">All snapshots are read-only. Nothing here is published.</p>
                </CardContent>
              </Card>
            )}

            {snapshotLoading && <Skeleton className="h-96 w-full" />}

            {snapshot && !snapshotLoading && (
              <Card className="border-stone-200" data-testid="archive-snapshot-viewer">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="space-y-1 min-w-0">
                      <CardTitle className="text-base font-heading text-stone-900 truncate">{snapshot.label}</CardTitle>
                      <CardDescription className="text-[12.5px]">
                        <span className="font-mono">{snapshot.original_path}</span> · {snapshot.date} · commit <span className="font-mono">{snapshot.commit}</span> · {snapshot.line_count} lines
                      </CardDescription>
                      {snapshot.summary && (
                        <p className="text-[12px] text-stone-500 max-w-2xl leading-relaxed pt-1">{snapshot.summary}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant={viewMode === 'copy' ? 'default' : 'outline'}
                        onClick={() => setViewMode('copy')}
                        className={`rounded-full ${viewMode === 'copy' ? 'bg-[#3F617E] hover:bg-[#345470]' : ''}`}
                        data-testid="archive-view-copy-btn"
                      >
                        Show copy
                      </Button>
                      <Button
                        size="sm"
                        variant={viewMode === 'raw' ? 'default' : 'outline'}
                        onClick={() => setViewMode('raw')}
                        className={`rounded-full ${viewMode === 'raw' ? 'bg-[#3F617E] hover:bg-[#345470]' : ''}`}
                        data-testid="archive-view-raw-btn"
                      >
                        Show JSX
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleCopy}
                        className="rounded-full"
                        data-testid="archive-copy-btn"
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre
                    className="text-[12.5px] leading-relaxed bg-stone-50/70 border border-stone-200 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-[70vh] overflow-y-auto font-mono"
                    data-testid="archive-snapshot-content"
                  >
                    {visibleText || '(empty)'}
                  </pre>
                </CardContent>
              </Card>
            )}

            {selectedId && (
              <div className="mt-4">
                <Button variant="ghost" size="sm" onClick={() => { setSelectedId(null); setSnapshot(null); }} className="text-stone-500" data-testid="archive-back-btn">
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Clear selection
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </DeskLayout>
  );
};

export default ContentArchive;
